import React, { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { WemoloButton, WemoloCrossIcon, WemoloTypography } from '../Tokens'
import styles from './WemoloModal.module.css'

type WemoloModalSize = 'small' | 'medium' | 'large' | 'fullscreen'

interface WemoloModalProps {
    isOpen: boolean
    onClose: () => void
    /** When omitted, the header bar is hidden and the close button floats over the body. */
    title?: React.ReactNode
    children: React.ReactNode
    size?: WemoloModalSize
    /** Removes the default body padding — useful when children manage their own layout. */
    bodyPadding?: 'default' | 'none'
    closeOnOverlayClick?: boolean
    closeOnEscape?: boolean
    className?: string
}

const sizeClassByName: Record<WemoloModalSize, string> = {
    small: styles.sizeSmall,
    medium: styles.sizeMedium,
    large: styles.sizeLarge,
    fullscreen: styles.sizeFullscreen
}

const FOCUSABLE_SELECTOR =
    'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), iframe, object, embed, [tabindex]:not([tabindex="-1"]), [contenteditable="true"]'

const getFocusableElements = (root: HTMLElement): HTMLElement[] =>
    Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        (el) => !el.hasAttribute('disabled') && el.getAttribute('aria-hidden') !== 'true'
    )

export const WemoloModal: React.FC<WemoloModalProps> = ({
    isOpen,
    onClose,
    title,
    children,
    size = 'medium',
    bodyPadding = 'default',
    closeOnOverlayClick = true,
    closeOnEscape = true,
    className
}) => {
    const dialogRef = useRef<HTMLDivElement | null>(null)

    useEffect(() => {
        if (!isOpen || !closeOnEscape) return undefined

        const handleKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose()
        }
        window.addEventListener('keydown', handleKey)
        return () => window.removeEventListener('keydown', handleKey)
    }, [isOpen, closeOnEscape, onClose])

    useEffect(() => {
        if (!isOpen) return undefined

        const previousOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        return () => {
            document.body.style.overflow = previousOverflow
        }
    }, [isOpen])

    useEffect(() => {
        if (!isOpen) return undefined

        const previouslyFocused = document.activeElement as HTMLElement | null
        const dialog = dialogRef.current
        if (dialog) {
            const focusables = getFocusableElements(dialog)
            ;(focusables[0] ?? dialog).focus()
        }
        return () => {
            previouslyFocused?.focus?.()
        }
    }, [isOpen])

    if (!isOpen) return null

    const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
        if (closeOnOverlayClick && event.target === event.currentTarget) onClose()
    }

    const handleDialogKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key !== 'Tab' || !dialogRef.current) return
        const focusables = getFocusableElements(dialogRef.current)
        if (focusables.length === 0) {
            event.preventDefault()
            return
        }
        const first = focusables[0]
        const last = focusables[focusables.length - 1]
        const active = document.activeElement as HTMLElement | null
        if (event.shiftKey && active === first) {
            event.preventDefault()
            last.focus()
        } else if (!event.shiftKey && active === last) {
            event.preventDefault()
            first.focus()
        }
    }

    const hasHeader = title !== undefined

    return createPortal(
        <div className={styles.overlay} onClick={handleOverlayClick} role="presentation">
            <div
                ref={dialogRef}
                className={`${styles.dialog} ${sizeClassByName[size]} ${className ?? ''}`}
                role="dialog"
                aria-modal="true"
                tabIndex={-1}
                onKeyDown={handleDialogKeyDown}
            >
                {hasHeader ? (
                    <div className={styles.header}>
                        <WemoloTypography variant="headline-medium" tag="h2" className={styles.title}>
                            {title}
                        </WemoloTypography>
                        <WemoloButton
                            view="ghost"
                            size="small"
                            icon={<WemoloCrossIcon />}
                            aria-label="Close"
                            onClick={onClose}
                        />
                    </div>
                ) : (
                    <div className={styles.closeButtonFloating}>
                        <WemoloButton
                            view="ghost"
                            size="small"
                            icon={<WemoloCrossIcon />}
                            aria-label="Close"
                            onClick={onClose}
                        />
                    </div>
                )}
                <div className={`${styles.body} ${bodyPadding === 'none' ? styles.bodyNoPadding : ''}`}>{children}</div>
            </div>
        </div>,
        document.body
    )
}
